import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Flex, Heading, Text, Button, useToast, HStack, VStack, SimpleGrid,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer, Spinner, Tooltip, Badge,
    Switch, Select, Input, IconButton, Link, Tag,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
    ModalCloseButton, useDisclosure,
} from '@chakra-ui/react';
import { FaSyncAlt, FaSearch } from 'react-icons/fa';
import { BrainCircuit } from 'lucide-react';
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
    getCompraInteligenteDashboard, getCompraInteligenteAnalises, getCompraInteligenteAnalise,
    reanalisarCompraInteligente, atualizarConfigCompraInteligente,
} from '../services/api';
import { formatBRL, formatDateTime } from '../utils/formatters';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

const ACCENT = '#F68633';

const PERIODOS = [
    { dias: 7, label: '7 dias' },
    { dias: 15, label: '15 dias' },
    { dias: 30, label: '30 dias' },
    { dias: 90, label: '90 dias' },
];

const STATUS_ANALISE = {
    pendente: { label: 'Pendente', color: 'gray' },
    processando: { label: 'Processando', color: 'blue' },
    concluida: { label: 'Concluída', color: 'green' },
    erro: { label: 'Erro', color: 'red' },
};

const RESULTADO_ITEM = {
    economia: { label: 'Economia', color: 'green' },
    sem_economia: { label: 'Sem economia', color: 'gray' },
    sem_alternativa: { label: 'Sem alternativa', color: 'gray' },
    sem_custo_origem: { label: 'Sem custo na origem', color: 'orange' },
};

const CONFIANCA = {
    alta: { label: 'Confiança alta', color: 'green' },
    media: { label: 'Confiança média', color: 'yellow' },
    baixa: { label: 'Confiança baixa', color: 'red' },
};

const fmtInt = (v) => Number(v || 0).toLocaleString('pt-BR');
const fmtUsd = (v, casas = 2) =>
    'US$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });

const StatCard = ({ label, value, hint, valueColor = 'gray.800' }) => (
    <Box bg="white" borderWidth="1px" borderRadius="lg" p={4}>
        <Text fontSize="sm" color="gray.500">{label}</Text>
        <Text fontSize="2xl" fontWeight="bold" color={valueColor}>{value}</Text>
        {hint && <Text fontSize="xs" color="gray.400" mt={1}>{hint}</Text>}
    </Box>
);

const StatusBadge = ({ status }) => {
    const cfg = STATUS_ANALISE[status] || { label: status, color: 'gray' };
    return <Badge colorScheme={cfg.color}>{cfg.label}</Badge>;
};

const ResultadoBadge = ({ resultado }) => {
    const cfg = RESULTADO_ITEM[resultado] || { label: resultado, color: 'gray' };
    return <Badge colorScheme={cfg.color}>{cfg.label}</Badge>;
};

const origemVenda = (venda) => {
    if (!venda) return '-';
    return venda.origem + (venda.conta ? ` (conta ${venda.conta})` : '');
};

const CompraInteligente = () => {
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [dias, setDias] = useState(30);
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);

    const [analises, setAnalises] = useState([]);
    const [paginationMeta, setPaginationMeta] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [loadingLista, setLoadingLista] = useState(false);
    const [filtroStatus, setFiltroStatus] = useState('');
    const [filtroEconomia, setFiltroEconomia] = useState(false);
    const [buscaVenda, setBuscaVenda] = useState('');

    const [detalhe, setDetalhe] = useState(null);
    const [loadingDetalhe, setLoadingDetalhe] = useState(false);
    const [salvandoConfig, setSalvandoConfig] = useState(false);

    const fornecedores = useMemo(
        () => dados?.fornecedores || detalhe?.fornecedores || {},
        [dados, detalhe]
    );
    const nomeFornecedor = useCallback(
        (id) => (id ? (fornecedores[id] || `Fornecedor ${id}`) : '-'),
        [fornecedores]
    );

    const carregarDashboard = useCallback(async (d) => {
        setLoading(true);
        try {
            const { data } = await getCompraInteligenteDashboard({ dias: d });
            setDados(data);
        } catch (e) {
            toast({ title: 'Erro ao carregar o dashboard.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    const carregarAnalises = useCallback(async (d, page, status, economia, busca) => {
        setLoadingLista(true);
        try {
            const { data } = await getCompraInteligenteAnalises({
                dias: d,
                page,
                status: status || undefined,
                economia: economia ? 1 : undefined,
                busca: busca || undefined,
            });
            setAnalises(data.data || []);
            setPaginationMeta({ current_page: data.current_page, last_page: data.last_page, total: data.total });
        } catch (e) {
            toast({ title: 'Erro ao carregar as análises.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setLoadingLista(false);
        }
    }, [toast]);

    useEffect(() => { carregarDashboard(dias); }, [carregarDashboard, dias]);
    useEffect(() => {
        carregarAnalises(dias, currentPage, filtroStatus, filtroEconomia, buscaVenda);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carregarAnalises, dias, currentPage, filtroStatus, filtroEconomia]);

    const atualizarTudo = () => {
        carregarDashboard(dias);
        carregarAnalises(dias, currentPage, filtroStatus, filtroEconomia, buscaVenda);
    };

    const abrirDetalhe = async (id) => {
        setLoadingDetalhe(true);
        onOpen();
        try {
            const { data } = await getCompraInteligenteAnalise(id);
            setDetalhe(data);
        } catch (e) {
            toast({ title: 'Erro ao carregar o detalhe da análise.', status: 'error', duration: 4000, isClosable: true });
            onClose();
        } finally {
            setLoadingDetalhe(false);
        }
    };

    const reanalisar = async (id) => {
        try {
            await reanalisarCompraInteligente(id);
            toast({ title: 'Reanálise enfileirada — o resultado aparece em instantes.', status: 'success', duration: 4000, isClosable: true });
            onClose();
            setDetalhe(null);
            atualizarTudo();
        } catch (e) {
            toast({ title: 'Erro ao enfileirar a reanálise.', status: 'error', duration: 4000, isClosable: true });
        }
    };

    const toggleAtivo = async () => {
        const novo = !dados?.config?.ativo;
        setSalvandoConfig(true);
        try {
            await atualizarConfigCompraInteligente({ ativo: novo });
            setDados((prev) => (prev ? { ...prev, config: { ...prev.config, ativo: novo } } : prev));
            toast({
                title: novo ? 'Radar ligado: vendas novas serão analisadas.' : 'Radar desligado.',
                status: novo ? 'success' : 'info',
                duration: 4000,
                isClosable: true,
            });
        } catch (e) {
            toast({ title: 'Erro ao salvar a configuração.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setSalvandoConfig(false);
        }
    };

    const buscarPorVenda = () => {
        setCurrentPage(1);
        carregarAnalises(dias, 1, filtroStatus, filtroEconomia, buscaVenda);
    };

    const totais = dados?.totais || {};
    const porFornecedor = dados?.por_fornecedor || [];
    const porDestino = dados?.por_destino || [];
    const topEconomias = dados?.top_economias || [];
    const porDia = dados?.por_dia || [];

    const chartData = {
        labels: porDia.map((d) => {
            const [, m, dd] = String(d.dia).split('-');
            return `${dd}/${m}`;
        }),
        datasets: [{
            label: 'Economia (R$)',
            data: porDia.map((d) => Number(d.economia_total || 0)),
            backgroundColor: '#38A169',
            borderRadius: 4,
        }],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => ` ${formatBRL(ctx.parsed.y)} · ${fmtInt(porDia[ctx.dataIndex]?.analises)} vendas analisadas`,
                },
            },
        },
        scales: {
            y: { beginAtZero: true, ticks: { callback: (v) => `R$ ${v}` } },
        },
    };

    const analiseDetalhe = detalhe?.analise;

    return (
        <Box p={{ base: 4, md: 6 }} maxW="1300px" mx="auto">
            <Flex align="center" justify="space-between" mb={5} wrap="wrap" gap={3}>
                <HStack spacing={3}>
                    <BrainCircuit size={24} color={ACCENT} />
                    <Box>
                        <Heading size="md">Compra Inteligente</Heading>
                        <Text fontSize="sm" color="gray.500">
                            A cada venda, a IA procura o mesmo item (mesma marca) mais barato em outros fornecedores do catálogo.
                        </Text>
                    </Box>
                </HStack>
                <HStack spacing={3} wrap="wrap">
                    {dados && (
                        <HStack spacing={2} bg="white" borderWidth="1px" borderRadius="lg" px={3} py={1.5}>
                            <Switch
                                colorScheme="orange"
                                isChecked={!!dados?.config?.ativo}
                                onChange={toggleAtivo}
                                isDisabled={salvandoConfig}
                            />
                            <Text fontSize="sm" color="gray.600">
                                {dados?.config?.ativo ? 'Radar ligado' : 'Radar desligado'}
                            </Text>
                        </HStack>
                    )}
                    {PERIODOS.map((p) => (
                        <Button
                            key={p.dias}
                            size="sm"
                            variant={dias === p.dias ? 'solid' : 'outline'}
                            bg={dias === p.dias ? ACCENT : undefined}
                            color={dias === p.dias ? 'white' : undefined}
                            _hover={dias === p.dias ? { bg: ACCENT } : undefined}
                            onClick={() => { setDias(p.dias); setCurrentPage(1); }}
                        >
                            {p.label}
                        </Button>
                    ))}
                    <Button size="sm" leftIcon={<FaSyncAlt />} onClick={atualizarTudo} isLoading={loading}>
                        Atualizar
                    </Button>
                </HStack>
            </Flex>

            {loading && !dados ? (
                <Flex justify="center" py={20}><Spinner size="lg" color={ACCENT} /></Flex>
            ) : (
                <>
                    <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
                        <StatCard
                            label="Vendas analisadas"
                            value={fmtInt(totais.concluidas)}
                            hint={`${fmtInt(totais.em_andamento)} em andamento · ${fmtInt(totais.erros)} com erro`}
                        />
                        <StatCard
                            label="Itens com economia"
                            value={fmtInt(totais.itens_com_economia)}
                            hint={`de ${fmtInt(totais.itens)} itens analisados`}
                        />
                        <StatCard
                            label="Economia potencial"
                            value={formatBRL(totais.economia_total)}
                            valueColor="green.600"
                            hint="no custo de compra, se comprado no melhor fornecedor"
                        />
                        <StatCard
                            label="Custo de IA"
                            value={fmtUsd(totais.custo_ia_usd)}
                            hint="também aparece em Custos IA (origem compra inteligente)"
                        />
                    </SimpleGrid>

                    {porDia.length > 0 && (
                        <Box bg="white" borderWidth="1px" borderRadius="lg" p={4} mb={6}>
                            <Text fontWeight="semibold" mb={3}>Economia potencial por dia</Text>
                            <Box h="200px"><Bar data={chartData} options={chartOptions} /></Box>
                        </Box>
                    )}

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} mb={6}>
                        <Box bg="white" borderWidth="1px" borderRadius="lg" p={4}>
                            <Text fontWeight="semibold" mb={3}>Por fornecedor de origem (onde vendemos)</Text>
                            <TableContainer>
                                <Table size="sm">
                                    <Thead>
                                        <Tr>
                                            <Th>Fornecedor</Th>
                                            <Th isNumeric>Itens</Th>
                                            <Th isNumeric>Com economia</Th>
                                            <Th isNumeric>Economia</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {porFornecedor.map((r) => (
                                            <Tr key={r.fornecedor_id}>
                                                <Td>{nomeFornecedor(r.fornecedor_id)}</Td>
                                                <Td isNumeric>{fmtInt(r.itens)}</Td>
                                                <Td isNumeric>{fmtInt(r.com_economia)}</Td>
                                                <Td isNumeric fontWeight="semibold" color="green.600">{formatBRL(r.economia_total)}</Td>
                                            </Tr>
                                        ))}
                                        {porFornecedor.length === 0 && (
                                            <Tr><Td colSpan={4}><Text color="gray.500">Nenhum item analisado no período.</Text></Td></Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </Box>
                        <Box bg="white" borderWidth="1px" borderRadius="lg" p={4}>
                            <Text fontWeight="semibold" mb={3}>Melhores destinos de compra (onde está mais barato)</Text>
                            <TableContainer>
                                <Table size="sm">
                                    <Thead>
                                        <Tr>
                                            <Th>Fornecedor</Th>
                                            <Th isNumeric>Itens mais baratos</Th>
                                            <Th isNumeric>Economia</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {porDestino.map((r) => (
                                            <Tr key={r.fornecedor_id}>
                                                <Td>{nomeFornecedor(r.fornecedor_id)}</Td>
                                                <Td isNumeric>{fmtInt(r.itens)}</Td>
                                                <Td isNumeric fontWeight="semibold" color="green.600">{formatBRL(r.economia_total)}</Td>
                                            </Tr>
                                        ))}
                                        {porDestino.length === 0 && (
                                            <Tr><Td colSpan={3}><Text color="gray.500">Nenhuma economia encontrada no período.</Text></Td></Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </SimpleGrid>

                    <Box bg="white" borderWidth="1px" borderRadius="lg" p={4} mb={6}>
                        <Text fontWeight="semibold" mb={3}>Maiores economias do período</Text>
                        <TableContainer>
                            <Table size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>Produto</Th>
                                        <Th>Venda</Th>
                                        <Th>De → Para</Th>
                                        <Th isNumeric>Custo un. (de → para)</Th>
                                        <Th isNumeric>Economia</Th>
                                        <Th></Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {topEconomias.map((i) => (
                                        <Tr key={i.id}>
                                            <Td maxW="340px">
                                                <Text noOfLines={1} fontSize="sm">{i.produto_nome}</Text>
                                                <Text fontSize="xs" color="gray.500">{i.marca || 'sem marca'} · {i.codigo_global || '-'} · {fmtInt(i.quantidade)} un</Text>
                                            </Td>
                                            <Td>
                                                <Link href={`/vendas?busca=${i.analise?.venda_id}`} target="_blank" color="blue.500" fontSize="sm">
                                                    #{i.analise?.venda_id}
                                                </Link>
                                            </Td>
                                            <Td fontSize="sm">{nomeFornecedor(i.fornecedor_id)} → <b>{nomeFornecedor(i.melhor_fornecedor_id)}</b></Td>
                                            <Td isNumeric fontSize="sm">{formatBRL(i.valor_compra_origem)} → {formatBRL(i.melhor_valor_compra)}</Td>
                                            <Td isNumeric fontWeight="semibold" color="green.600">{formatBRL(i.economia_total)}</Td>
                                            <Td>
                                                <Button size="xs" variant="outline" onClick={() => abrirDetalhe(i.analise_id)}>Detalhes</Button>
                                            </Td>
                                        </Tr>
                                    ))}
                                    {topEconomias.length === 0 && (
                                        <Tr><Td colSpan={6}><Text color="gray.500">Nenhuma economia encontrada no período.</Text></Td></Tr>
                                    )}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Box bg="white" borderWidth="1px" borderRadius="lg" p={4}>
                        <Flex align="center" justify="space-between" mb={3} wrap="wrap" gap={3}>
                            <Text fontWeight="semibold">Análises por venda</Text>
                            <HStack spacing={3} wrap="wrap">
                                <HStack spacing={2}>
                                    <Switch
                                        size="sm"
                                        colorScheme="green"
                                        isChecked={filtroEconomia}
                                        onChange={(e) => { setFiltroEconomia(e.target.checked); setCurrentPage(1); }}
                                    />
                                    <Text fontSize="sm" color="gray.600">Só com economia</Text>
                                </HStack>
                                <Select
                                    size="sm"
                                    w="160px"
                                    placeholder="Todos os status"
                                    value={filtroStatus}
                                    onChange={(e) => { setFiltroStatus(e.target.value); setCurrentPage(1); }}
                                >
                                    <option value="concluida">Concluída</option>
                                    <option value="pendente">Pendente</option>
                                    <option value="processando">Processando</option>
                                    <option value="erro">Erro</option>
                                </Select>
                                <HStack spacing={1}>
                                    <Input
                                        size="sm"
                                        w="130px"
                                        placeholder="Nº da venda"
                                        value={buscaVenda}
                                        onChange={(e) => setBuscaVenda(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && buscarPorVenda()}
                                    />
                                    <IconButton size="sm" aria-label="Buscar" icon={<FaSearch />} onClick={buscarPorVenda} />
                                </HStack>
                            </HStack>
                        </Flex>
                        {loadingLista ? (
                            <Flex justify="center" py={10}><Spinner color={ACCENT} /></Flex>
                        ) : (
                            <>
                                <TableContainer>
                                    <Table size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th>Venda</Th>
                                                <Th>Data da venda</Th>
                                                <Th>Origem</Th>
                                                <Th>Status</Th>
                                                <Th isNumeric>Itens</Th>
                                                <Th isNumeric>Com economia</Th>
                                                <Th isNumeric>Economia</Th>
                                                <Th></Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {analises.map((a) => (
                                                <Tr key={a.id} bg={Number(a.itens_com_economia) > 0 ? 'green.50' : undefined}>
                                                    <Td>
                                                        <Link href={`/vendas?busca=${a.venda_id}`} target="_blank" color="blue.500">
                                                            #{a.venda_id}
                                                        </Link>
                                                    </Td>
                                                    <Td fontSize="sm">{a.venda?.data_venda ? formatDateTime(a.venda.data_venda) : formatDateTime(a.created_at)}</Td>
                                                    <Td fontSize="sm">{origemVenda(a.venda)}</Td>
                                                    <Td><StatusBadge status={a.status} /></Td>
                                                    <Td isNumeric>{fmtInt(a.total_itens)}</Td>
                                                    <Td isNumeric>{fmtInt(a.itens_com_economia)}</Td>
                                                    <Td isNumeric fontWeight={Number(a.economia_total) > 0 ? 'semibold' : 'normal'} color={Number(a.economia_total) > 0 ? 'green.600' : 'gray.500'}>
                                                        {formatBRL(a.economia_total)}
                                                    </Td>
                                                    <Td>
                                                        <HStack spacing={1} justify="flex-end">
                                                            <Button size="xs" variant="outline" onClick={() => abrirDetalhe(a.id)}>
                                                                Detalhes
                                                            </Button>
                                                            <Tooltip label="Reanalisar esta venda">
                                                                <IconButton
                                                                    size="xs"
                                                                    variant="ghost"
                                                                    aria-label="Reanalisar"
                                                                    icon={<FaSyncAlt />}
                                                                    onClick={() => reanalisar(a.id)}
                                                                />
                                                            </Tooltip>
                                                        </HStack>
                                                    </Td>
                                                </Tr>
                                            ))}
                                            {analises.length === 0 && (
                                                <Tr><Td colSpan={8}><Text color="gray.500">Nenhuma análise no período/filtros.</Text></Td></Tr>
                                            )}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                                {paginationMeta && paginationMeta.last_page > 1 && (
                                    <Flex justify="center" align="center" gap={3} mt={3}>
                                        <Button size="sm" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>
                                            Anterior
                                        </Button>
                                        <Text fontSize="sm">Página {currentPage} de {paginationMeta.last_page}</Text>
                                        <Button size="sm" onClick={() => setCurrentPage((p) => Math.min(paginationMeta.last_page, p + 1))} isDisabled={currentPage === paginationMeta.last_page}>
                                            Próximo
                                        </Button>
                                    </Flex>
                                )}
                            </>
                        )}
                    </Box>
                </>
            )}

            {/* ── Modal de detalhe da análise ── */}
            <Modal isOpen={isOpen} onClose={() => { onClose(); setDetalhe(null); }} size="5xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader borderBottomWidth="1px" pb={3}>
                        {analiseDetalhe ? (
                            <Flex align="center" gap={3} wrap="wrap">
                                <Text>Análise da venda #{analiseDetalhe.venda_id}</Text>
                                <StatusBadge status={analiseDetalhe.status} />
                                <Text fontSize="sm" color="gray.500" fontWeight="normal">
                                    {origemVenda(analiseDetalhe.venda)} · {analiseDetalhe.venda?.data_venda ? formatDateTime(analiseDetalhe.venda.data_venda) : '-'}
                                </Text>
                            </Flex>
                        ) : 'Análise'}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody bg="gray.50" py={5}>
                        {loadingDetalhe || !analiseDetalhe ? (
                            <Flex justify="center" py={16}><Spinner size="lg" color={ACCENT} /></Flex>
                        ) : (
                            <VStack align="stretch" spacing={4}>
                                {analiseDetalhe.erro && (
                                    <Box bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="lg" p={3}>
                                        <Text fontSize="sm" color="red.700"><b>Erro:</b> {analiseDetalhe.erro}</Text>
                                    </Box>
                                )}
                                {analiseDetalhe.resumo && (
                                    <Box bg="white" borderWidth="1px" borderRadius="lg" p={3}>
                                        <Text fontSize="xs" color="gray.400" mb={1}>RESUMO DA IA</Text>
                                        <Text fontSize="sm">{analiseDetalhe.resumo}</Text>
                                    </Box>
                                )}

                                {(analiseDetalhe.itens || []).map((item) => (
                                    <Box key={item.id} bg="white" borderWidth="1px" borderRadius="lg" p={4}>
                                        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={2} mb={2}>
                                            <Box>
                                                <Text fontWeight="semibold" fontSize="sm">{item.produto_nome}</Text>
                                                <HStack spacing={2} mt={1} wrap="wrap">
                                                    <Tag size="sm" colorScheme="purple">{item.marca || 'sem marca'}</Tag>
                                                    <Tag size="sm">{nomeFornecedor(item.fornecedor_id)}</Tag>
                                                    <Text fontSize="xs" color="gray.500">cód. {item.codigo_global || '-'}</Text>
                                                    <Text fontSize="xs" color="gray.500">{fmtInt(item.quantidade)} un vendida(s)</Text>
                                                </HStack>
                                            </Box>
                                            <VStack align="flex-end" spacing={1}>
                                                <ResultadoBadge resultado={item.resultado} />
                                                {item.confianca && (
                                                    <Badge variant="subtle" colorScheme={CONFIANCA[item.confianca]?.color || 'gray'}>
                                                        {CONFIANCA[item.confianca]?.label || item.confianca}
                                                    </Badge>
                                                )}
                                            </VStack>
                                        </Flex>

                                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={3}>
                                            <Box>
                                                <Text fontSize="xs" color="gray.400">Custo na origem (un)</Text>
                                                <Text fontSize="sm" fontWeight="semibold">{item.valor_compra_origem != null ? formatBRL(item.valor_compra_origem) : 'não cadastrado'}</Text>
                                            </Box>
                                            <Box>
                                                <Text fontSize="xs" color="gray.400">Vendido por (un)</Text>
                                                <Text fontSize="sm">{item.valor_venda_unitario != null ? formatBRL(item.valor_venda_unitario) : '-'}</Text>
                                            </Box>
                                            <Box>
                                                <Text fontSize="xs" color="gray.400">Melhor custo encontrado (un)</Text>
                                                <Text fontSize="sm" fontWeight="semibold" color={item.resultado === 'economia' ? 'green.600' : 'gray.700'}>
                                                    {item.melhor_valor_compra != null ? `${formatBRL(item.melhor_valor_compra)} · ${nomeFornecedor(item.melhor_fornecedor_id)}` : '-'}
                                                </Text>
                                            </Box>
                                            <Box>
                                                <Text fontSize="xs" color="gray.400">Economia (total do item)</Text>
                                                <Text fontSize="sm" fontWeight="bold" color={item.resultado === 'economia' ? 'green.600' : 'gray.500'}>
                                                    {item.economia_total != null ? formatBRL(item.economia_total) : '-'}
                                                </Text>
                                            </Box>
                                        </SimpleGrid>

                                        {(item.alternativas || []).length > 0 && (
                                            <TableContainer mb={2}>
                                                <Table size="sm" variant="simple">
                                                    <Thead>
                                                        <Tr>
                                                            <Th>Fornecedor</Th>
                                                            <Th>Marca</Th>
                                                            <Th>Produto</Th>
                                                            <Th isNumeric>Custo un.</Th>
                                                            <Th isNumeric>Emb.</Th>
                                                            <Th isNumeric>Estoque</Th>
                                                            <Th>Mesmo item?</Th>
                                                            <Th>Observação da IA</Th>
                                                        </Tr>
                                                    </Thead>
                                                    <Tbody>
                                                        {item.alternativas.map((alt, idx) => (
                                                            <Tr key={idx} bg={alt.melhor ? 'green.50' : undefined}>
                                                                <Td fontSize="sm" fontWeight={alt.melhor ? 'semibold' : 'normal'}>
                                                                    {alt.fornecedor}{alt.melhor ? ' ★' : ''}
                                                                </Td>
                                                                <Td fontSize="sm">{alt.marca || '-'}</Td>
                                                                <Td fontSize="sm" maxW="260px">
                                                                    <Tooltip label={`${alt.nome || ''} (cód. ${alt.codigo_global || '-'})`}>
                                                                        <Text noOfLines={1}>{alt.nome || '-'}</Text>
                                                                    </Tooltip>
                                                                </Td>
                                                                <Td isNumeric fontSize="sm">{alt.valor_compra_unitario != null ? formatBRL(alt.valor_compra_unitario) : '-'}</Td>
                                                                <Td isNumeric fontSize="sm">{alt.quantidade_embalagem || 1}</Td>
                                                                <Td isNumeric fontSize="sm">{fmtInt(alt.estoque)}</Td>
                                                                <Td>
                                                                    <Badge colorScheme={alt.mesmo_item ? 'green' : 'red'} variant="subtle">
                                                                        {alt.mesmo_item ? (alt.mesma_marca ? 'Sim' : 'Sim, outra marca') : 'Não'}
                                                                    </Badge>
                                                                </Td>
                                                                <Td fontSize="xs" color="gray.600" maxW="280px" whiteSpace="normal">{alt.observacao || '-'}</Td>
                                                            </Tr>
                                                        ))}
                                                    </Tbody>
                                                </Table>
                                            </TableContainer>
                                        )}

                                        {item.justificativa && (
                                            <Text fontSize="xs" color="gray.500"><b>Conclusão da IA:</b> {item.justificativa}</Text>
                                        )}
                                    </Box>
                                ))}

                                <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                                    <Text fontSize="xs" color="gray.400">
                                        Modelo {analiseDetalhe.modelo || '-'} · custo {fmtUsd(analiseDetalhe.custo_ia_usd, 4)}
                                        {analiseDetalhe.concluida_em ? ` · concluída em ${formatDateTime(analiseDetalhe.concluida_em)}` : ''}
                                    </Text>
                                </Flex>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter borderTopWidth="1px">
                        <HStack>
                            {analiseDetalhe && (
                                <Button size="sm" leftIcon={<FaSyncAlt />} variant="outline" onClick={() => reanalisar(analiseDetalhe.id)}>
                                    Reanalisar
                                </Button>
                            )}
                            <Button size="sm" onClick={() => { onClose(); setDetalhe(null); }}>Fechar</Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default CompraInteligente;
